import type { CampaignConsultantDto, PlaybookDto, SavePlaybookRequest, SetConsultantRequest } from '@signalvote/shared';
import { campaignHeaders, defineQuery, defineWrite, edit } from '@/lib/data';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, type ApiErrorSpec } from './apiFetch';

/** The server's own sentence when it sent one (a 409 like "no pending wake-ups" reads as is), else the fallback. */
const serverSentence =
  (fallback: string): ApiErrorSpec =>
  (body, response) =>
    failureFromResponse(response, body, body.error || body.message ? undefined : fallback);

/** The signed-in consultant's own playbook. A save goes through the request's context, so its write set names ConsultantPlaybook. */
export const usePlaybook = defineQuery({
  key: () => ['playbook', 'mine'],
  reads: 'GET /api/assistant/playbook',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<PlaybookDto>('/api/assistant/playbook', { signal, headers: campaignHeaders(campaignId), error: "Couldn't load your playbook" }),
});

/** The saved playbook lands at once, so the next save sends its fresh version. */
export const useSavePlaybook = defineWrite({
  send: (request: SavePlaybookRequest) =>
    apiJson<PlaybookDto>('/api/assistant/playbook', { method: 'PUT', json: request, error: serverSentence("Couldn't save your playbook. Try again.") }),
  land: (saved) => [edit(usePlaybook, undefined, () => saved)],
});

export const useCampaignConsultant = defineQuery({
  key: (campaignId: string) => ['campaignConsultant', campaignId],
  reads: 'GET /api/campaigns/{campaignId}/consultant',
  fetch: (campaignId, { signal, campaignId: selected }) =>
    apiJson<CampaignConsultantDto>(`/api/campaigns/${campaignId}/consultant`, {
      signal,
      headers: campaignHeaders(selected),
      error: "Couldn't load the consultant",
    }),
});

export const useSetCampaignConsultant = defineWrite({
  send: ({ campaignId, request }: { campaignId: string; request: SetConsultantRequest }) =>
    apiJson<CampaignConsultantDto>(`/api/campaigns/${campaignId}/consultant`, {
      method: 'PUT',
      json: request,
      error: serverSentence("Couldn't save the consultant. Try again."),
    }),
  land: (saved, { campaignId }) => [edit(useCampaignConsultant, campaignId, () => saved)],
});
